// UploadReceiptModal — capture a receipt (camera or file/PDF) and upload.
// Single file (browse, or one camera shot that turns out to be a dup/failure):
// unchanged — closes on success, hands off to the list; a 409 shows the
// existing receipt as a blocking dup card.
// Multiple files (browse, multi-select): switches to a sequential upload
// queue — one row per file, uploaded one at a time so the server load stays
// sane. A 409 there just marks that row amber; it never blocks the rest.
// Camera: rapid-fire with a confirm step. Each capture shows an in-modal
// preview before it uploads — "Looks good" fires the upload in the background
// (fire-and-forget, doesn't block) and immediately rearms the camera;
// "Retake" discards the shot and rearms just as fast. The only serialized
// step is the human glance at the photo. Cancelling the OS camera sheet (a
// change event with no file) pauses the session at a resting "Take another /
// Done" state instead of reopening the camera. Plain capture for now;
// jscanify's scan-crop/deskew polish layers onto this component next.
// For one charge (`forCharge`, from Charges without a receipt): one file or one
// camera shot, no batch and no rapid-fire; the upload carries the charge id.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import useReceiptsStore from '../stores/useReceiptsStore';
import type { Receipt, MissingCharge } from './types';
import StatusPill from './StatusPill';
import { prepareForUpload } from './prepareUpload';
import { chargeSentence } from './MissingCharges';
import { Segmented } from '../components/FilterBar';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import Alert from '../components/ui/Alert';
import Panel from '../components/ui/Panel';
import LogoSpinner from '../components/ui/LogoSpinner';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';

const MAX_BYTES = 25 * 1024 * 1024;
// Mirrors the server's door (receipts-router.js acceptsFile), for folder picks: any
// image or PDF goes up; a HEIC converts on the way (prepareUpload.ts).
const RECEIPT_FILE_RE = /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?|pdf)$/i;
const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);
const formatBytes = (n: number) => (n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

type QueueState = 'waiting' | 'uploading' | 'done' | 'duplicate' | 'failed';
// `preview` is an object URL of the file as it would have gone up, so a row that
// did not make it can still be told apart by its picture; `file` is what Retry resends.
interface QueueItem { file: File; state: QueueState; error?: string; preview?: string; }
type Mode = 'pick' | 'queue' | 'camera';
type ShotState = 'done' | 'duplicate' | 'failed'; // a settled, confirmed upload's outcome
type ShotDisplay = 'uploading' | ShotState | null; // what the ticker actually shows

const ROUTING_CAPTION = "Scan anyone's receipts. Ones on someone else's card route to them automatically.";

// Two key recipes. Filled carries the dialog's next step; tonal is every alternative.
// The state layer is an overlay image, so hover reads right on either instance's accent.

interface Props {
  onClose: () => void;
  /** Refresh the list. `forCharge` is true when the upload was for one charge. */
  onUploaded: (forCharge?: boolean) => void;
  /** The receipt is for this exact statement charge. */
  forCharge?: MissingCharge;
}

export default function UploadReceiptModal({ onClose, onUploaded, forCharge }: Props) {
  const toast = useToast();
  const history = useHistory();
  const upload = useReceiptsStore((s) => s.upload);
  const formOptions = useReceiptsStore((s) => s.formOptions);
  const ensureFormOptions = useReceiptsStore((s) => s.ensureFormOptions);

  // "Bought for" (QBO class) for everything scanned this session — the scanner picks
  // it on the slider (there's no "auto" for interactive scans; that's folder-import
  // only). Defaults to the org-default class, sticks unless slid, or "Not sure" defers.
  const [classChoice, setClassChoice] = useState<number | 'notsure' | null>(null);
  useEffect(() => { ensureFormOptions(); }, [ensureFormOptions]);
  const classes = formOptions?.classes || [];
  useEffect(() => {
    if (classChoice == null && classes.length) {
      const def = classes.find((c) => c.is_default) || classes[0];
      if (def) setClassChoice(def.id);
    }
  }, [classes]); // eslint-disable-line react-hooks/exhaustive-deps
  const clsArg = () => (classChoice === 'notsure' ? { deferred: true } : classChoice != null ? { classId: classChoice } : {});
  const selectedClassLabel = classChoice === 'notsure' ? 'Not sure' : (classes.find((c) => c.id === classChoice)?.label || null);

  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<Mode>('pick');

  // Single-file path (browse-one, or camera-once that dup'd/failed before a
  // session was established) — untouched from the original behavior.
  const [uploading, setUploading] = useState(false);
  const [dup, setDup] = useState<Receipt | null>(null);    // 409 existing receipt

  // Batch queue (multi-select browse).
  const [queue, setQueue] = useState<QueueItem[] | null>(null);

  // Camera session (rapid-fire repeat captures with a confirm step).
  const [cameraCount, setCameraCount] = useState(0); // uploaded shots only — a retake doesn't count
  const [lastShot, setLastShot] = useState<ShotState | null>(null); // most recent settled upload's outcome
  const [pendingUploads, setPendingUploads] = useState(0); // uploads in flight; can overlap (fire-and-forget)
  const [cameraPaused, setCameraPaused] = useState(false); // OS sheet was cancelled — resting state
  const [capturedFile, setCapturedFile] = useState<File | null>(null); // awaiting Looks good / Retake
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const cameraBusy = pendingUploads > 0;
  // While anything is uploading, show that (even if a slower earlier shot hasn't
  // settled yet); once everything's settled, show whichever settled last.
  const shotDisplay: ShotDisplay = cameraBusy ? 'uploading' : lastShot;

  // Revoke the object URL whenever it's replaced or the modal unmounts.
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const queueDone = !!queue && queue.every((q) => q.state !== 'waiting' && q.state !== 'uploading');
  const batchRunning = !!queue && !queueDone;
  const closeDisabled = batchRunning || cameraBusy || uploading;
  const failedItems = useMemo(() => (queue || []).filter((q) => q.state === 'failed'), [queue]);

  // Every preview URL is released when the modal unmounts (rows come and go, so
  // per-row revoking would race Retry; a handful of object URLs costs nothing).
  const queueRef = useRef<QueueItem[] | null>(null);
  queueRef.current = queue;
  useEffect(() => () => { (queueRef.current || []).forEach((q) => { if (q.preview) URL.revokeObjectURL(q.preview); }); }, []);

  const summaryLine = useMemo(() => {
    if (!queue) return '';
    const done = queue.filter((q) => q.state === 'done').length;
    const duplicate = queue.filter((q) => q.state === 'duplicate').length;
    const failed = queue.filter((q) => q.state === 'failed').length;
    const parts = [`${done} added`];
    if (duplicate) parts.push(`${duplicate} already loaded`);
    if (failed) parts.push(`${failed} didn't upload`);
    return parts.join(', ');
  }, [queue]);

  // A preview the patch replaces is released here; a row that succeeds drops its
  // preview too, so a Retry that works leaves nothing behind.
  const updateQueueItem = (i: number, patch: Partial<QueueItem>) =>
    setQueue((q) => (q ? q.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, ...patch };
      if (next.state === 'done' || next.state === 'duplicate') next.preview = undefined;
      if (it.preview && it.preview !== next.preview) URL.revokeObjectURL(it.preview);
      return next;
    }) : q));

  // One queue row's upload, start to finish: convert and shrink, size-check what
  // would actually go up, send, settle the row. Shared by the first run and Retry.
  const uploadQueueItem = async (i: number, original: File) => {
    updateQueueItem(i, { state: 'uploading', error: undefined });
    const f = await prepareForUpload(original);
    if (f.size > MAX_BYTES) {
      updateQueueItem(i, { state: 'failed', error: 'Exceeds the 25 MB limit', preview: previewFor(f) });
      return;
    }
    try {
      await upload(f, clsArg());
      updateQueueItem(i, { state: 'done' });
      onUploaded();
    } catch (e: any) {
      if (e?.status === 409 && e?.data?.duplicate) {
        updateQueueItem(i, { state: 'duplicate' });
        onUploaded();
      } else {
        updateQueueItem(i, { state: 'failed', error: e?.message || "Didn't upload", preview: previewFor(f) });
      }
    }
  };
  // A preview only when the browser can show the bytes (a HEIC that could not be
  // converted has no picture to offer; the row still carries its name and reason).
  const previewFor = (f: File) => (f.type.startsWith('image/') && !/hei[cf]/i.test(f.type) ? URL.createObjectURL(f) : undefined);

  const handleFile = async (orig: File | null) => {
    if (!orig) return;
    setUploading(true);
    setDup(null);
    try {
      const f = await prepareForUpload(orig);
      if (f.size > MAX_BYTES) { toast.error('File exceeds the 25 MB limit'); return; }
      const res = (await upload(f, clsArg(), forCharge?.id)) as Receipt & { linked_existing?: boolean };
      if (forCharge && res?.linked_existing) {
        // Same bytes as a receipt already on file: the server linked that one instead.
        toast.success('Linked the receipt you already uploaded to this charge');
        onUploaded(true);
        onClose();
        return;
      }
      // Hand off to the list — it shows the spinner, then the "detection finished"
      // check as the digest completes. No in-modal review step. Unless something
      // from an earlier batch is still sitting here unsent: then the dialog stays
      // open, because closing it would take those rows with it.
      toast.success('Receipt added, reading it now…');
      onUploaded(!!forCharge);
      if (!failedItems.length) onClose();
    } catch (e: any) {
      if (e?.status === 409 && e?.data?.duplicate) {
        setDup(e.data.duplicate as Receipt);
      } else if (forCharge && [403, 404, 409].includes(e?.status)) {
        // The charge is gone (matched, settled, or no longer mine): nothing to attach to.
        toast.error(e?.data?.error?.message || e?.message || 'That charge is already settled.');
        onUploaded(true);
        onClose();
      } else {
        toast.error(e?.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  // Sequential on purpose — keeps server load sane and reuses the same
  // per-file 409 handling as the single-file path, just routed to a row
  // instead of a blocking card. Rows that did not make it last time stay at the
  // top of the new batch, so nothing that failed is ever out of sight.
  const runQueue = async (files: File[]) => {
    setMode('queue');
    const carried = (queueRef.current || []).filter((q) => q.state === 'failed');
    setQueue([...carried, ...files.map((file) => ({ file, state: 'waiting' as QueueState }))]);
    for (let i = 0; i < files.length; i++) {
      await uploadQueueItem(carried.length + i, files[i]);
    }
  };

  const retryQueueItem = async (i: number) => {
    const item = queueRef.current?.[i];
    if (!item || item.state !== 'failed') return;
    setMode('queue');
    await uploadQueueItem(i, item.file);
  };

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (files.length === 1) { handleFile(files[0]); return; }
    runQueue(Array.from(files));
  };

  // A folder pick hands back EVERYTHING in it (and in its subfolders): sidecar
  // files, dotfiles, spreadsheets. Keep only what the server would accept, so the
  // queue shows receipts, not a wall of "Upload failed" rows.
  const handleFolderSelected = (files: FileList | null) => {
    const keep = Array.from(files || []).filter((f) => {
      const name = f.name.split('/').pop() || f.name;
      return !name.startsWith('.') && RECEIPT_FILE_RE.test(name);
    });
    if (keep.length === 0) { toast.error('No receipt images or PDFs in that folder'); return; }
    if (keep.length === 1) { handleFile(keep[0]); return; }
    runQueue(keep);
  };

  // Just captures — no upload yet. The shot sits in preview until the user
  // confirms it (a blurry shot caught here beats one caught at digest time,
  // especially now that receipts can route away to someone else's queue).
  const handleCameraFile = (f: File | null) => {
    if (forCharge) {
      // One shot for one charge: no session. Cancelling the sheet goes back to the buttons.
      if (!f) { setMode('pick'); return; }
      setCapturedFile(f);
      setPreviewUrl(URL.createObjectURL(f));
      setMode('camera');
      return;
    }
    if (!f) {
      // OS camera sheet was cancelled with no shot taken — pause the rapid-fire
      // session at the resting state instead of auto-rearming. (Before the very
      // first shot, mode is still 'pick' and this is a no-op, same as before.)
      if (mode === 'camera') setCameraPaused(true);
      return;
    }
    setMode('camera');
    setCameraPaused(false);
    setCapturedFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  // Fire-and-forget — doesn't block the next capture. Concurrent shots can be
  // in flight together; the ticker just reflects whichever settles last.
  const uploadConfirmedShot = async (orig: File) => {
    setPendingUploads((n) => n + 1);
    try {
      const f = await prepareForUpload(orig); // the size check runs on what would go up, after the downscale
      if (f.size > MAX_BYTES) { toast.error('Photo exceeds the 25 MB limit'); setLastShot('failed'); return; }
      await upload(f, clsArg());
      setCameraCount((n) => n + 1);
      setLastShot('done');
      onUploaded();
    } catch (e: any) {
      setLastShot(e?.status === 409 && e?.data?.duplicate ? 'duplicate' : 'failed');
    } finally {
      setPendingUploads((n) => n - 1);
    }
  };

  const confirmShot = () => {
    const f = capturedFile;
    setCapturedFile(null);
    setPreviewUrl(null);
    if (forCharge) {
      // The single-file path: closes on success, shows the duplicate card on a 409.
      setMode('pick');
      if (f) handleFile(f);
      return;
    }
    if (f) uploadConfirmedShot(f);
    cameraRef.current?.click();
  };

  const retakeShot = () => {
    setCapturedFile(null);
    setPreviewUrl(null);
    if (forCharge) setMode('pick');
    cameraRef.current?.click();
  };

  // Back to the buttons, keeping what failed: those rows show under the buttons
  // with a Retry, and ride along at the top of the next batch.
  const resetToPick = () => {
    setQueue((q) => {
      (q || []).forEach((it) => { if (it.state !== 'failed' && it.preview) URL.revokeObjectURL(it.preview); });
      const kept = (q || []).filter((it) => it.state === 'failed');
      return kept.length ? kept : null;
    });
    setMode('pick');
  };
  // Done closes the dialog, except when rows that did not upload are still here:
  // then it returns to the buttons, where those rows are shown with their Retry.
  const finishUp = () => { onUploaded(); if (failedItems.length) resetToPick(); else onClose(); };

  return (
    <Dialog open title="Add a receipt" onClose={onClose} busy={closeDisabled} dirty={!!capturedFile || failedItems.length > 0} size="md">
        <div className="space-y-4">
          {forCharge && (
            <p className="text-sm text-on-surface">{chargeSentence(forCharge)}</p>
          )}
          {/* hidden inputs */}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden m-0"
                 onChange={(e) => { handleCameraFile(e.target.files?.[0] || null); e.target.value = ''; }} />
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple={!forCharge} className="hidden m-0"
                 onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }} />
          {/* webkitdirectory is not in React's input typings; the spread keeps tsc quiet. */}
          <input ref={folderRef} type="file" multiple className="hidden m-0" {...({ webkitdirectory: '' } as object)}
                 onChange={(e) => { handleFolderSelected(e.target.files); e.target.value = ''; }} />

          {mode === 'pick' && !dup && (
            <FadeIn className="space-y-4">
              {classes.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-on-surface-variant">Bought for</div>
                  <Segmented
                    label="Bought for"
                    value={classChoice === 'notsure' ? 'notsure' : classChoice != null ? String(classChoice) : ''}
                    onChange={(k) => setClassChoice(k === 'notsure' ? 'notsure' : Number(k))}
                    options={[
                      ...classes.map((c) => ({ key: String(c.id), label: c.label })),
                      { key: 'notsure', label: 'Not sure' },
                    ]}
                  />
                  <p className="text-[11px] text-on-surface-variant">Applies to everything you scan now.</p>
                </div>
              )}
              <button
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                className={`${buttonClass('filled', 'lg')} w-full`}
              >
                {uploading ? 'Uploading…' : 'Take a photo'}
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className={`${buttonClass('quiet', 'lg')} w-full`}
              >
                Choose a file or PDF
              </button>
              {!forCharge && (
                <button
                  onClick={() => folderRef.current?.click()}
                  disabled={uploading}
                  className={`${buttonClass('quiet', 'lg')} w-full`}
                >
                  Load a whole folder
                </button>
              )}
              {forCharge ? (
                <p className="text-xs text-on-surface-variant text-center">
                  We'll read it in the background and put it beside this charge for the bookkeeper.
                </p>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-on-surface-variant text-center">
                    We'll read it in the background. It'll appear in your list in a few seconds. Pick more than one file, or a folder, to load a batch.
                  </p>
                  <p className="text-xs text-on-surface-variant text-center">{ROUTING_CAPTION}</p>
                </div>
              )}
              <Reveal show={failedItems.length > 0 && !!queue}>
                <div className="space-y-1.5">
                  <div className="text-xs font-semibold text-red-300">
                    {failedItems.length === 1 ? "1 file didn't upload" : `${failedItems.length} files didn't upload`}
                  </div>
                  {queue?.map((item, i) => (item.state === 'failed'
                    ? <QueueRow key={i} item={item} onRetry={() => retryQueueItem(i)} />
                    : null))}
                </div>
              </Reveal>
            </FadeIn>
          )}

          {/* Duplicate hard-block (single-file path only) */}
          {mode === 'pick' && dup && (
            <FadeIn className="space-y-3">
              <Alert tone="warning" variant="note">
                You've already loaded this receipt.
              </Alert>
              <Panel label="Existing receipt" on="panel" padding="sm" className="text-sm text-on-surface">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{dup.vendor_name || 'Unknown vendor'}</span>
                  <StatusPill status={dup.status} className="shrink-0" />
                </div>
                <div className="text-on-surface-variant text-xs mt-0.5">
                  {dup.receipt_date || '—'} · <span className="font-mono tabular-nums">{money(dup.total)}</span>
                </div>
              </Panel>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDup(null)} className={buttonClass('quiet', 'md')}>Upload a different one</button>
                <button onClick={() => history.push(`/receipts/${dup.public_id}`)}
                        className={buttonClass('filled', 'md')}>
                  Open it
                </button>
              </div>
            </FadeIn>
          )}

          {/* Batch queue (multi-select browse) */}
          {mode === 'queue' && queue && (
            <FadeIn className="space-y-3">
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
                {queue.map((item, i) => <QueueRow key={i} item={item} onRetry={() => retryQueueItem(i)} />)}
              </div>
              <Reveal show={queueDone}>
                <div className="space-y-3">
                  <div className="text-sm text-on-surface text-center">{summaryLine}</div>
                  <div className="flex justify-end gap-3 pt-1">
                    <button onClick={resetToPick} className={buttonClass('quiet', 'md')}>Add more</button>
                    <button onClick={finishUp}
                            className={buttonClass('filled', 'md')}>
                      Done
                    </button>
                  </div>
                </div>
              </Reveal>
            </FadeIn>
          )}

          {/* Camera session — rapid-fire with a confirm step. Looks good/Retake
              both rearm the camera immediately; the ticker below tracks the
              confirmed shot's upload in the background. Cancelling the OS
              camera sheet drops to a paused resting state. */}
          {mode === 'camera' && previewUrl && (
            <div className="space-y-3">
              <img
                src={previewUrl}
                alt="Captured receipt"
                className="w-full max-h-[45vh] object-contain rounded-lg ring-1 ring-outline-variant bg-surface-container"
              />
              <div className="flex gap-3">
                <button
                  onClick={retakeShot}
                  className={`${buttonClass('quiet', 'lg')} flex-1`}
                >
                  Retake
                </button>
                <button
                  onClick={confirmShot}
                  className={`${buttonClass('filled', 'lg')} flex-1`}
                >
                  Looks good
                </button>
              </div>
            </div>
          )}

          {mode === 'camera' && !previewUrl && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-1.5 py-2">
                <ShotStatus state={shotDisplay} />
                <span className="text-sm text-on-surface">
                  <span className="text-primary font-semibold tabular-nums">{cameraCount}</span> scanned this session
                </span>
                {selectedClassLabel && (
                  <span className="text-xs text-on-surface-variant">Bought for <span className="text-on-surface">{selectedClassLabel}</span></span>
                )}
              </div>
              {cameraPaused ? (
                <>
                  <button
                    onClick={() => { setCameraPaused(false); cameraRef.current?.click(); }}
                    className={`${buttonClass('quiet', 'lg')} w-full`}
                  >
                    Take another photo
                  </button>
                  <div className="flex justify-end">
                    <button onClick={finishUp} className={buttonClass('quiet', 'md')}>Done</button>
                  </div>
                </>
              ) : (
                <button
                  onClick={finishUp}
                  className={`${buttonClass('filled', 'lg')} w-full`}
                >
                  Done
                </button>
              )}
              <p className="text-xs text-on-surface-variant text-center">{ROUTING_CAPTION}</p>
            </div>
          )}

        </div>
    </Dialog>
  );
}

// One file in the batch. A row that did not make it shows its picture, the reason,
// and a Retry: the person recognizes the receipt by the photo, not by IMG_9290.
function QueueRow({ item, onRetry }: { item: QueueItem; onRetry: () => void }) {
  const failed = item.state === 'failed';
  return (
    <div className={`rounded-md px-3 py-2 flex items-center gap-3 ${failed ? 'bg-red-500/10 ring-1 ring-inset ring-red-500/25' : 'bg-surface-container'}`}>
      {failed && (
        <div className="shrink-0 w-11 h-11 rounded-md bg-surface-container-highest ring-1 ring-inset ring-outline-variant overflow-hidden flex items-center justify-center">
          {item.preview
            ? <img src={item.preview} alt="" className="w-full h-full object-cover" />
            : <span className="text-[10px] text-on-surface-variant">No preview</span>}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-on-surface truncate">{item.file.name}</div>
        <div className={`text-xs truncate ${failed ? 'text-red-300/90' : 'text-on-surface-variant'}`}>
          {failed && item.error ? item.error : formatBytes(item.file.size)}
        </div>
      </div>
      {failed ? (
        <button type="button" onClick={onRetry}
                className={`${buttonClass('filled', 'sm')} shrink-0`}>
          Retry
        </button>
      ) : <QueueBadge state={item.state} />}
    </div>
  );
}

function QueueBadge({ state }: { state: QueueState }) {
  if (state === 'waiting') {
    return <span className="text-xs text-on-surface-variant shrink-0">waiting</span>;
  }
  if (state === 'uploading') {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full text-[11px] px-2.5 py-0.5 ring-1 ring-inset bg-surface-container-highest text-on-surface-variant ring-outline-variant shrink-0">
        <LogoSpinner size="inline" />
        uploading
      </span>
    );
  }
  if (state === 'done') {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full text-[11px] px-2.5 py-0.5 ring-1 ring-inset bg-green-500/15 text-green-300 ring-green-500/30 shrink-0">
        <span className="font-bold">✓</span> added
      </span>
    );
  }
  if (state === 'duplicate') {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-full text-[11px] px-2.5 py-0.5 ring-1 ring-inset bg-amber-900/30 text-amber-300 ring-amber-800/60 shrink-0">
        already loaded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full text-[11px] px-2.5 py-0.5 ring-1 ring-inset bg-red-500/15 text-red-300 ring-red-500/30 shrink-0">
      failed
    </span>
  );
}

// Most recent confirmed shot's upload state, shown in the rapid-fire session view.
function ShotStatus({ state }: { state: ShotDisplay }) {
  if (state === 'uploading') {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-on-surface">
        <LogoSpinner size="inline" />
        Uploading…
      </span>
    );
  }
  if (state === 'done') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-green-300">
        <span className="font-bold">✓</span> Added
      </span>
    );
  }
  if (state === 'duplicate') {
    return <span className="text-sm text-amber-300">Already loaded</span>;
  }
  if (state === 'failed') {
    return <span className="text-sm text-red-300">Failed</span>;
  }
  return <span className="text-sm text-on-surface-variant">Ready</span>;
}
